/**
 * Edgeguard Phase — attacker fires a hitscan railgun beam at the tumbling
 * defender after DI resolves, before defensive movement begins.
 *
 * During edgeguard (hitstun-driven window):
 * - Defender: physics running (tumble, gravity, terrain bounce) — hitstunFrames
 *   count down naturally, driving the window exit
 * - Attacker: aims freely, weapon-driven auto-charge, fire when charged
 * - Aim assist: gentle pull toward defender(s) based on input mode
 * - Camera: follows primary defender
 * - Railgun: instant hitscan beam that carves terrain, bounces off walls/ceiling,
 *   damages + knocks back all players along its path (no secondary DI)
 *
 * After edgeguard resolves → startDefensivePhase() for knocked players.
 */

import { Input, Keys } from 'bonkjs';
import { EDGEGUARD, PHYSICS, AIM, TANK_VISUAL, TURN_FLOW, ACTION, TUMBLE, WORLD, HITLAG, KNOCKBACK, SIDE_DEATH } from '../constants';
import type { GameState, Player } from '../types';
import type { InputProvider } from '../input';
import type { GameEffect } from './effects';
import { transitionPhase } from './effects';
import { startDefensivePhase } from './defense';
import { clearCircle, isPointInTerrain } from '../terrain/grid';
import { spawnSmoothingRegion } from '../terrain/sand-physics';
import { unsettleTanksNearCrater } from '../physics/tanks';
import { enterTumble } from '../physics/tanks';
import { lookupTank } from '../tanks/registry';
import { mirrorEnterTumble } from '../physics/transitions';
import { cancelGrapple } from './grapple';
import { recordEdgeguardHit } from './trick-score';
import { LABEL_VARIANTS } from '../constants';

// ─── Beam constants ──────────────────────────────────────────────────────────

const BEAM_MAX_LENGTH = 3000;       // max trace distance (world px)
const BEAM_MAX_BOUNCES = 2;         // wall/ceiling bounces
const BEAM_STEP_SIZE = 4;           // trace step (world px)
const BEAM_WIDTH = 8;               // hit detection & render width
const BEAM_CARVE_RADIUS = 12;       // terrain destruction radius per step
const BEAM_DISPLAY_TIME = 0.5;      // seconds to show beam after fire
const BEAM_DAMAGE = 8;              // damage % per hit player
const BEAM_KNOCKBACK = 9;           // knockback force
const BEAM_TERMINUS_BLAST = 20;     // crater radius at beam end
const BEAM_COLOR = 0x00ffff;        // cyan

/** Convert milliseconds to fixed-update frame count (60fps). */
const msToFrames = (ms: number) => Math.round(ms * 60 / 1000);

/** Get aim assist pull strength based on current input mode. */
function getAimAssistStrength(): number {
  const mode = Input.inputMode;
  if (mode === 'keyboard') return EDGEGUARD.AIM_ASSIST_STRENGTH_KB;
  if (mode === 'gamepad' || mode === 'touch') return EDGEGUARD.AIM_ASSIST_STRENGTH_GP;
  return EDGEGUARD.AIM_ASSIST_STRENGTH_MOUSE;
}

// ─── Phase handler ───────────────────────────────────────────────────────────

export function updateEdgeguardPhase(
  state: GameState,
  input: InputProvider,
  fx: GameEffect[],
): void {
  // Skip while frozen (hitlag from initial explosion may still be active)
  if (state.frameCount < state.freezeUntil) return;

  // Tick beam display timer
  if (state.edgeguardBeam) {
    state.edgeguardBeam.timer -= 1 / 60;
    if (state.edgeguardBeam.timer <= 0) {
      state.edgeguardBeam = null;
    }
  }

  // Camera follows primary defender (first in knocked list)
  if (state.edgeguardKnockedIndices.length > 0) {
    const defIdx = state.edgeguardKnockedIndices[0];
    const def = state.players[defIdx];
    if (def.alive) {
      fx.push({ type: 'camera_follow', x: def.x, y: def.y });
    }
  }

  const attacker = state.players[state.currentPlayer];

  // === Attacker: aim + weapon-driven charge + instant fire ===
  if (!state.edgeguardFired) {
    // Aim angle (Arrow UP/DOWN — same smoothstep ramp as aiming.ts)
    const aimUp = input.getKey(Keys.ArrowUp);
    const aimDown = input.getKey(Keys.ArrowDown);

    if (aimUp || aimDown) {
      attacker.aimHeldFrames++;
      const t = Math.min(attacker.aimHeldFrames / AIM.RAMP_FRAMES, 1);
      const smooth = t * t * (3 - 2 * t);
      const speed = AIM.MIN_SPEED + (AIM.MAX_SPEED - AIM.MIN_SPEED) * smooth;
      const aimDir = aimUp ? 1 : -1;
      attacker.angle += aimDir * speed;
      if (attacker.angle > AIM.MAX_ANGLE) attacker.angle -= Math.PI * 2;
      if (attacker.angle < AIM.MIN_ANGLE) attacker.angle += Math.PI * 2;
    } else {
      attacker.aimHeldFrames = 0;
    }

    // Aim assist — gentle pull toward nearest defender within capture zone
    const assistStrength = getAimAssistStrength();
    if (assistStrength > 0) {
      let closestAngle = -1;
      let closestDist = Infinity;
      for (const di of state.edgeguardKnockedIndices) {
        const def = state.players[di];
        if (!def.alive) continue;
        const toAngle = Math.atan2(-(def.y - attacker.y), def.x - attacker.x);
        // Normalize to aim range
        const clampedTo = toAngle >= AIM.MIN_ANGLE ? toAngle : toAngle + Math.PI * 2;
        // Wrap-aware angular distance (handles seam at straight-down wrap point)
        const rawDist = Math.abs(clampedTo - attacker.angle);
        const dist = Math.min(rawDist, Math.PI * 2 - rawDist);
        if (dist < EDGEGUARD.AIM_ASSIST_ANGLE && dist < closestDist) {
          closestAngle = clampedTo;
          closestDist = dist;
        }
      }
      if (closestAngle >= 0) {
        attacker.angle += (closestAngle - attacker.angle) * assistStrength;
        if (attacker.angle > AIM.MAX_ANGLE) attacker.angle -= Math.PI * 2;
        if (attacker.angle < AIM.MIN_ANGLE) attacker.angle += Math.PI * 2;
      }
    }

    // Auto-charge: ramp 0 → 1 over weapon's chargeFrames
    state.edgeguardChargeFrame = Math.min(state.edgeguardChargeFrame + 1, state.edgeguardChargeTarget);
    const chargeT = state.edgeguardChargeTarget > 0
      ? state.edgeguardChargeFrame / state.edgeguardChargeTarget
      : 1;
    attacker.power = chargeT;
    attacker.charging = chargeT < 1;

    const charged = chargeT >= 1;

    // Instant fire on any press once charged (edge-trigger)
    const firePressed = input.getKey(ACTION.FIRE) || input.getKey(ACTION.CHARGE);
    const justPressed = firePressed && !attacker.fireHeld;
    attacker.fireHeld = firePressed;

    if (charged && justPressed) {
      fireRailgunBeam(state, attacker, fx);
    }
  }

  // === Exit conditions (hitstun-driven) ===
  const beamDone = state.edgeguardFired && state.edgeguardBeam === null;
  const allExitedHitstun = state.edgeguardKnockedIndices.every(
    i => !state.players[i].alive || state.players[i].hitstunFrames <= 0,
  );
  const noAliveDefenders = state.edgeguardKnockedIndices.every(
    i => !state.players[i].alive,
  );

  if (beamDone || allExitedHitstun || noAliveDefenders) {
    // Clean up beam if exiting before beam finished
    state.edgeguardBeam = null;

    // Reset attacker charge visual
    attacker.charging = false;
    attacker.power = 0;

    fx.push({ type: 'edgeguard_hide' });

    // Transition to defensive movement for knocked players
    const aliveKnocked = state.edgeguardKnockedIndices.filter(
      i => state.players[i].alive && state.players[i].tumble,
    );

    if (aliveKnocked.length > 0) {
      transitionPhase(state, 'defensive_movement', fx);
      startDefensivePhase(state, aliveKnocked, fx);
    } else {
      transitionPhase(state, 'awaiting_next_turn', fx);
      state.turnTimer = TURN_FLOW.TURN_DELAY;
    }
  }
}

// ─── Hitscan beam fire ───────────────────────────────────────────────────────

export function fireRailgunBeam(
  state: GameState,
  attacker: Player,
  fx: GameEffect[],
): void {
  state.edgeguardFired = true;
  attacker.charging = false;
  attacker.power = 0;

  // Turret tip = beam start point
  const fireAngle = attacker.angle; // 0 = right, PI = left
  const cosA = Math.cos(fireAngle);
  const sinA = Math.sin(fireAngle);
  const startX = attacker.x + cosA * TANK_VISUAL.TURRET_LENGTH;
  const startY = attacker.y - sinA * TANK_VISUAL.TURRET_LENGTH;

  // Trace beam path (hitscan — instant, carves terrain, bounces off walls)
  const traceResult = traceBeamPath(startX, startY, fireAngle, state);
  const beamPath = traceResult.path;

  // Store beam for rendering
  state.edgeguardBeam = {
    path: beamPath,
    timer: BEAM_DISPLAY_TIME,
    maxTimer: BEAM_DISPLAY_TIME,
    width: BEAM_WIDTH,
    color: BEAM_COLOR,
  };

  // Apply line damage to all players along beam
  const hitPlayers = new Set<number>();

  for (let seg = 0; seg < beamPath.length - 1; seg++) {
    const p1 = beamPath[seg];
    const p2 = beamPath[seg + 1];

    for (let pi = 0; pi < state.players.length; pi++) {
      if (pi === state.currentPlayer) continue; // don't hit self
      if (hitPlayers.has(pi)) continue;          // only hit each player once

      const target = state.players[pi];
      if (!target.alive) continue;

      const dist = pointToSegmentDistance(target.x, target.y, p1.x, p1.y, p2.x, p2.y);
      const ecb = lookupTank(target.tankId).ecbRadius ?? PHYSICS.ECB_RADIUS;
      if (dist < ecb + BEAM_WIDTH) {
        hitPlayers.add(pi);

        // Apply damage
        target.damagePercent += BEAM_DAMAGE;

        // Record edgeguard hit into trick session
        recordEdgeguardHit(state, pi, target.x, target.y, BEAM_DAMAGE, fx);

        // Knockback direction = beam travel direction (kamehameha — sends them flying)
        const segDx = p2.x - p1.x;
        const segDy = p2.y - p1.y;
        const segLen = Math.sqrt(segDx * segDx + segDy * segDy);
        const nx = segLen > 0 ? segDx / segLen : 0;
        const ny = segLen > 0 ? segDy / segLen : 1;

        // Smash-style: knockback scales with accumulated damage %
        const percentMult = 1 + target.damagePercent / KNOCKBACK.KBG;
        const force = BEAM_KNOCKBACK * percentMult;

        // Cancel grapple if this player is being pulled
        if (state.grapple.active?.playerIndex === pi) {
          cancelGrapple(state);
        }

        // Override velocity — beam slams them in its travel direction (not additive)
        target.vx = nx * force;
        target.vy = ny * force;

        // Enter tumble
        enterTumble(target, {
          hitstunPerSpeed: TUMBLE.HITSTUN_PER_SPEED,
          minHitstun: TUMBLE.MIN_HITSTUN,
          maxHitstun: TUMBLE.MAX_HITSTUN,
          speedGated: true,
          damagePercent: target.damagePercent,
        });
        mirrorEnterTumble(target, 'knockback');

        // Add to knocked indices if not already there
        if (!state.edgeguardKnockedIndices.includes(pi)) {
          state.edgeguardKnockedIndices.push(pi);
        }

        // Hit visual effects — Sakurai 3-layer contrast (dark backing + bright + white pop)
        fx.push({ type: 'particles', method: 'spawnRichExplosion', args: [target.x, target.y, 0x002233, 25, 0.8] }); // dark framing
        fx.push({ type: 'particles', method: 'spawnRichExplosion', args: [target.x, target.y, 0xffffff, 50, 0.6] }); // bright
        fx.push({ type: 'particles', method: 'spawnHitCross', args: [target.x, target.y, 40] });
        fx.push({ type: 'tank_flash', playerIndex: pi, bright: 0x00ffff, dark: 0x002222, duration: 0.3 });
        // Edgeguard hit text — pick variant deterministically
        const followUpVariants = LABEL_VARIANTS['FOLLOW-UP!'] ?? ['FOLLOW-UP!'];
        const followUpText = followUpVariants[Math.abs(state.frameCount) % followUpVariants.length];
        fx.push({ type: 'announce_world', text: followUpText, x: target.x, y: target.y - 50, color: 0x00ffff, fontSize: 36 });
      }
    }
  }

  // Push terrain carve update from beam trace
  if (traceResult.carveBounds) {
    fx.push({ type: 'terrain_update', bounds: traceResult.carveBounds });
    spawnSmoothingRegion(state, traceResult.carveBounds);
  }

  // Terminus crater (only if enabled)
  const terminus = beamPath[beamPath.length - 1];
  if (EDGEGUARD.BEAM_TERMINUS_CRATER) {
    const terminusBounds = clearCircle(terminus.x, terminus.y, BEAM_TERMINUS_BLAST);
    if (terminusBounds) {
      fx.push({ type: 'terrain_update', bounds: terminusBounds });
      fx.push({ type: 'particles', method: 'spawnTerrainDebris', args: [terminus.x, terminus.y, BEAM_TERMINUS_BLAST, 0.5] });
      spawnSmoothingRegion(state, terminusBounds);
      unsettleTanksNearCrater(state.players, terminus.x, BEAM_TERMINUS_BLAST);
    }
  }

  // Delayed end flash at terminus — Sakurai Phase 4 punctuation
  fx.push({ type: 'particles', method: 'spawnEdgeguardEndFlash', args: [terminus.x, terminus.y] });

  // Sparks along beam path — alternating dark/bright (Sakurai: mix darker elements)
  for (let idx = 0; idx < beamPath.length; idx++) {
    const pt = beamPath[idx];
    if (idx % 2 === 0) {
      fx.push({ type: 'particles', method: 'spawnRichExplosion', args: [pt.x, pt.y, 0x001122, 10, 0.3] });
    }
    fx.push({ type: 'particles', method: 'spawnRichExplosion', args: [pt.x, pt.y, BEAM_COLOR, 15, 0.3] });
  }

  // Hitstop on hit
  if (hitPlayers.size > 0) {
    state.freezeUntil = Math.max(state.freezeUntil, state.frameCount + msToFrames(HITLAG.HITLAG_MS));
    fx.push({ type: 'screen_shake', intensity: HITLAG.HIT_SHAKE, decay: HITLAG.HIT_SHAKE_DECAY });
  }

  // Big screen effects
  fx.push({ type: 'screen_flash', color: 0xffffff, alpha: 0.3 });
  fx.push({ type: 'screen_flash', color: BEAM_COLOR, alpha: 0.15 });
  fx.push({ type: 'screen_shake', intensity: 12, decay: 0.82 });

  // Muzzle flash at turret tip
  const muzzleAngle = -fireAngle;
  // Dark framing at muzzle (Sakurai: mix darker elements behind bright)
  fx.push({ type: 'particles', method: 'spawnRichExplosion', args: [startX, startY, 0x001122, 20, 0.6] });
  // Impact lines at muzzle (Sakurai: straight lines vs curves)
  fx.push({ type: 'particles', method: 'spawnEdgeguardFireLines', args: [startX, startY, muzzleAngle] });
  fx.push({ type: 'particles', method: 'spawnMuzzleFlash', args: [startX, startY, muzzleAngle, attacker.color] });
  fx.push({ type: 'particles', method: 'spawnRichExplosion', args: [startX, startY, BEAM_COLOR, 40, 0.8] });

  // Include target snapshots so the receiving client can snap targets before
  // running hit detection — prevents beam hitting on one client and missing
  // on the other due to target position drift during tumble.
  const targetSnaps = state.players
    .map((p, i) => i !== state.currentPlayer && p.alive ? { playerIndex: i, x: p.x, y: p.y, momentum: p.momentum, damagePercent: p.damagePercent } : null)
    .filter((s): s is NonNullable<typeof s> => s !== null);
  fx.push({ type: 'edgeguard_fired', angle: fireAngle, x: attacker.x, y: attacker.y, targets: targetSnaps });
}

// ─── Beam path tracing ───────────────────────────────────────────────────────

interface TraceResult {
  path: { x: number; y: number }[];
  carveBounds: { minGx: number; maxGx: number; minGy: number; maxGy: number } | null;
}

/**
 * Trace a hitscan beam from (startX, startY) along angle.
 * Bounces off walls and ceiling. Carves through terrain.
 * Stops at void or max length. Returns polyline + terrain carve bounds.
 */
function traceBeamPath(
  startX: number,
  startY: number,
  angle: number,
  state: GameState,
): TraceResult {
  const points: { x: number; y: number }[] = [{ x: startX, y: startY }];
  let x = startX;
  let y = startY;
  let dx = Math.cos(angle);
  let dy = -Math.sin(angle); // negative: canvas Y increases downward
  let remaining = BEAM_MAX_LENGTH;
  let bounces = 0;

  // Track terrain bounds carved for a single terrain_update push
  let minCarveGx = Infinity, maxCarveGx = -Infinity;
  let minCarveGy = Infinity, maxCarveGy = -Infinity;
  let carved = false;

  while (remaining > 0 && bounces <= BEAM_MAX_BOUNCES) {
    let hitBoundary = false;
    const steps = Math.ceil(remaining / BEAM_STEP_SIZE);

    for (let step = 0; step < steps; step++) {
      const nextX = x + dx * BEAM_STEP_SIZE;
      const nextY = y + dy * BEAM_STEP_SIZE;

      // Wall bounce / termination (left/right world edges)
      if (nextX <= WORLD.WORLD_LEFT || nextX >= WORLD.WORLD_RIGHT) {
        if (SIDE_DEATH.OPEN_SIDES) {
          // Open sides — beam terminates at edge
          points.push({ x: nextX <= WORLD.WORLD_LEFT ? WORLD.WORLD_LEFT : WORLD.WORLD_RIGHT, y: nextY });
          const carveBounds = carved
            ? { minGx: minCarveGx, maxGx: maxCarveGx, minGy: minCarveGy, maxGy: maxCarveGy }
            : null;
          return { path: points, carveBounds };
        }
        // Walls on — horizontal reflection bounce
        x = nextX <= WORLD.WORLD_LEFT ? WORLD.WORLD_LEFT + 1 : WORLD.WORLD_RIGHT - 1;
        y = nextY;
        points.push({ x, y });
        dx = -dx;
        bounces++;
        hitBoundary = true;
        remaining -= step * BEAM_STEP_SIZE;
        break;
      }

      // Ceiling bounce
      if (nextY <= WORLD.WORLD_TOP) {
        x = nextX;
        y = WORLD.WORLD_TOP + 1;
        points.push({ x, y });
        dy = -dy; // vertical reflection
        bounces++;
        hitBoundary = true;
        remaining -= step * BEAM_STEP_SIZE;
        break;
      }

      // Void termination
      if (nextY >= state.voidY) {
        points.push({ x: nextX, y: state.voidY });
        const carveBounds = carved
          ? { minGx: minCarveGx, maxGx: maxCarveGx, minGy: minCarveGy, maxGy: maxCarveGy }
          : null;
        return { path: points, carveBounds };
      }

      // Terrain — beam passes through (carve only if enabled)
      if (EDGEGUARD.BEAM_CARVE_TERRAIN && isPointInTerrain(nextX, nextY, true)) {
        const bounds = clearCircle(nextX, nextY, BEAM_CARVE_RADIUS);
        if (bounds) {
          carved = true;
          minCarveGx = Math.min(minCarveGx, bounds.minGx);
          maxCarveGx = Math.max(maxCarveGx, bounds.maxGx);
          minCarveGy = Math.min(minCarveGy, bounds.minGy);
          maxCarveGy = Math.max(maxCarveGy, bounds.maxGy);
        }
      }

      x = nextX;
      y = nextY;
      remaining -= BEAM_STEP_SIZE;
    }

    if (!hitBoundary) {
      points.push({ x, y });
      break;
    }
  }

  const carveBounds = carved
    ? { minGx: minCarveGx, maxGx: maxCarveGx, minGy: minCarveGy, maxGy: maxCarveGy }
    : null;
  return { path: points, carveBounds };
}

// ─── Geometry helpers ────────────────────────────────────────────────────────

/** Distance from point (px, py) to line segment (x1,y1)-(x2,y2). */
function pointToSegmentDistance(
  px: number, py: number,
  x1: number, y1: number,
  x2: number, y2: number,
): number {
  const cp = closestPointOnSegment(px, py, x1, y1, x2, y2);
  const ddx = px - cp.x;
  const ddy = py - cp.y;
  return Math.sqrt(ddx * ddx + ddy * ddy);
}

/** Closest point on segment (x1,y1)-(x2,y2) to (px,py). */
function closestPointOnSegment(
  px: number, py: number,
  x1: number, y1: number,
  x2: number, y2: number,
): { x: number; y: number } {
  const A = px - x1;
  const B = py - y1;
  const C = x2 - x1;
  const D = y2 - y1;
  const dot = A * C + B * D;
  const lenSq = C * C + D * D;

  let t = lenSq !== 0 ? dot / lenSq : -1;
  t = Math.max(0, Math.min(1, t));

  return { x: x1 + t * C, y: y1 + t * D };
}
